import { useState, ChangeEvent, FormEvent, useRef } from 'react'
import axios from 'axios'
import cns from 'classnames'
import { Select } from '../Select'
import { NETWORK_TONEN_OPTIONS_MAP } from './constants'

import * as cn from './styles.module.scss'

const validateAddress = (value: string) =>
  /^0x[a-fA-F0-9]{40}$/.test(value)
    ? null
    : 'Please enter a valid ethereum address'

type AddressData = {
  value: string
  error: string | null
}

type Props = {
  network: 'kovan'
}

export const TokenForm = ({ network }: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [txHash, setTxHash] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [addressData, setAddressData] = useState<AddressData>({
    value: '',
    error: null,
  })

  const tokenOptions = useRef(NETWORK_TONEN_OPTIONS_MAP[network])

  const [coinType, setCoinType] = useState(tokenOptions.current[0].value)

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    setAddressData({ value, error: validateAddress(value) })
  }

  const handleCoinTypeChange = (value: string) => setCoinType(value)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const error = validateAddress(addressData.value)
    if (error) {
      setAddressData({ value: addressData.value, error })
      return
    }
    setTxHash(null)
    setFormError(null)
    setIsSubmitting(true)
    axios
      .get(`${process.env.API_URL}/${network}-faucet`, {
        params: { address: addressData.value, token: coinType },
      })
      .then(response => {
        if (response?.data.success) {
          setTxHash(response.data.tx)
        } else if (response.data.message) {
          setFormError(response.data.message)
        } else {
          setFormError('Something went wrong.')
        }
      })
      .catch(response => {
        setFormError(response.response.data)
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <form onSubmit={handleSubmit}>
      {formError && <p className={cns(cn.alert, cn.error)}>{formError}</p>}
      {txHash && (
        <p className={cns(cn.alert, cn.success)}>
          Tokens were sent. You can see transaction{' '}
          <a
            href={`https://${network}.etherscan.io/tx/${txHash}`}
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
      )}
      <div className={cn.inputRow}>
        <label htmlFor="address-input" className={cn.label}>
          {network} testnet account address
        </label>
        <input
          className={cns(cn.input, { [cn.error]: addressData.error })}
          id="address-input"
          placeholder="e.g. 0x71c7656ec7ab88b098defb751b7401b5f6d8976f"
          value={addressData.value}
          onChange={handleAddressChange}
        />
        {addressData.error && (
          <p className={cn.errorMessage}>{addressData.error}</p>
        )}
      </div>
      <div className={cn.inputRow}>
        <label htmlFor="coin-select" className={cn.label}>
          Coin
        </label>

        <Select
          value={coinType}
          options={tokenOptions.current}
          onChange={handleCoinTypeChange}
        />
      </div>
      <button type="submit" disabled={isSubmitting} className={cn.button}>
        {isSubmitting ? 'Processing...' : 'Send request'}
      </button>
    </form>
  )
}
